import { useMemo, useState } from "react";
import FormBuilderFieldSelection from "./FormBuilderFieldSelection";
import FormBuilderCustomization from "./FormBuilderCustomization";
import { Field } from "../../types/TenantInterfaces";
import { Prefill } from "../../types/GlobalInterfaces";
import AppDrawer from "../Drawers/AppDrawer";
import { Button } from "reactstrap";
import {
  FormBuilder,
  FormBuilderField,
} from "../../types/FormBuilderInterfaces";
import useModule from "../../hooks/useModule";
import FormBuilderModuleSelection from "./FormBuilderModuleSelection";
import useCreatePublicForm from "../../api/api-hooks/publicForms/useCreatePublicForm";
import useUpdatePublicForm from "../../api/api-hooks/publicForms/useUpdatePublicForm";
import { difference, union } from "underscore";
import { AppConfirm } from "../../utils/PopupUtil";
import AppSpinner from "../../elements/AppSpinner";

export interface FormAvailableFields {
  label: string;
  value: string;
  group: string;
  item: Field;
}

interface Props {
  open: boolean;
  onClose: () => void;
  moduleAPIName?: string;
  existingForm: FormBuilder | null;
  prefill?: Prefill;
  setSuccessFormID: (formID: string) => void;
  setShare?: (val: boolean) => void;
}

const unsupportedFields = ["Calculated"];

const FormBuilderDrawer = ({
  open,
  onClose,
  moduleAPIName,
  existingForm,
  prefill,
  setSuccessFormID,
  setShare,
}: Props) => {
  const { createPublicForm, createPublicFormError, createPublicFormLoading } =
    useCreatePublicForm();
  const { updatePublicForm, updatePublicFormError, updateID } =
    useUpdatePublicForm();

  const saving = createPublicFormLoading || !!updateID;

  const [form, setForm] = useState<FormBuilder>(
    existingForm ? existingForm : getInitialForm(moduleAPIName, prefill)
  );
  const { module: mod } = useModule(form.ModuleAPIName);
  const [step, setStep] = useState<string>(
    existingForm
      ? "form-customization"
      : moduleAPIName
      ? "select-fields"
      : "select-module"
  );

  const handleSetForm = (
    value: { [key: string]: FormBuilderField } | any,
    key: string
  ) => {
    setForm({ ...form, [key]: value });
  };

  const handleSelectField = (fields: any[]) => {
    const fieldsObject: any = {};
    for (const field of fields) {
      fieldsObject[field.APIName] = {
        Field: field,
        Settings: {
          Label: field.Name,
          Placeholder: field.Name,
          Required: false,
          Match: false,
        },
      };
    }

    handleSetForm(fieldsObject, "Fields");
  };

  const handleClose = () => {
    onClose();
  };

  const availableOptions = useMemo(() => {
    if (!mod) return [];

    const hidden = form?.HiddenFields ? Object.keys(form.HiddenFields) : [];

    const options = mod.Fields.flatMap((item: Field) => {
      if (
        unsupportedFields.includes(item.FieldType) ||
        item.SystemField === true ||
        hidden.includes(item.APIName)
      ) {
        return [];
      }

      return [
        {
          label: item.Name,
          value: item.APIName,
          group: item.FieldType,
          item: item,
        },
      ];
    });

    return options;
  }, [mod, form?.HiddenFields]);

  const handleSuccess = (formID: string) => setSuccessFormID(formID);

  const handleSave = (exitEarly = false) => {
    const fields = mod?.Fields ?? [];
    const visible = form.Fields ? Object.keys(form.Fields) : [];
    const hidden = form.HiddenFields ? Object.keys(form.HiddenFields) : [];
    const req = fields.flatMap((f: any) =>
      f.Required === true ? [f.APIName] : []
    );

    const formFields = union(visible, hidden);
    const missingRequiredFields = difference(req, formFields);

    if (missingRequiredFields.length > 0) {
      return AppConfirm({
        title: "Missing Required Fields",
        html: `Your form is missing required fields! <br/> Either add them to form, or add them as hidden fields. <br/> Fields/Hidden fields can be added in the "Build Tab"`,
        icon: "error",
        confirmButtonColor: "var(--primary)",
        confirmButtonText: `&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;OK&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;`,
        showCancelButton: false,
      });
    }

    if (existingForm) {
      updatePublicForm({ updatedPublicForm: form, onSuccess: handleSuccess });

      if (exitEarly) return;
    } else {
      createPublicForm({ form, onSuccess: handleSuccess });
    }
    setForm(
      existingForm ? existingForm : getInitialForm(moduleAPIName, prefill)
    );
    setStep(moduleAPIName ? "select-fields" : "select-module");
    handleClose();
  };

  const handleSelectModule = (value: string) => {
    setForm({
      ...form,
      ModuleAPIName: value,
      Header: { ...form.Header, Name: `${value} Public Form` },
    });
  };

  return (
    <AppDrawer
      placement="right"
      full
      backdrop="static"
      open={open}
      onClose={handleClose}
      headerTitle={mod?.Name ? `${mod?.Name} Form` : "Public Form"}
      headerOptions={
        <>
          {step === "form-customization" && (
            <>
              {existingForm && (
                <Button
                  color="light"
                  onClick={() => {
                    handleSave(true);
                    setSuccessFormID(existingForm.id as string);
                    if (setShare) {
                      setShare(true);
                    }
                  }}
                  size="sm"
                  style={{ width: 80 }}
                >
                  Share
                </Button>
              )}
              <Button
                color="primary"
                size="sm"
                // @ts-ignore
                onClick={handleSave}
                style={{ width: 110 }}
                disabled={saving}
              >
                {saving ? <AppSpinner color="white" /> : "Save Form"}
              </Button>
            </>
          )}
        </>
      }
      bodyCSS="p-0 rs-drawer-no-pad-thx"
      disablePadding
      drawerStyle={{
        width: "100%",
        transition: "width 0.05s ease-in",
      }}
      body={
        <>
          {step === "select-module" && (
            <FormBuilderModuleSelection
              moduleAPIName={form.ModuleAPIName}
              handleSelectModule={(value) => handleSelectModule(value)}
              changeStep={(step: string) => setStep(step)}
            />
          )}
          {step === "select-fields" && (
            <FormBuilderFieldSelection
              changeStep={(step: string) => setStep(step)}
              form={form}
              handleSelectField={handleSelectField}
              availableOptions={availableOptions}
            />
          )}
          {step === "form-customization" && (
            <FormBuilderCustomization
              setForm={(
                value: { [key: string]: FormBuilderField },
                key: string
              ) => handleSetForm(value, key)}
              form={form}
              prefill={prefill}
              availableOptions={availableOptions}
              module={mod ?? undefined}
            />
          )}
        </>
      }
    />
  );
};

function getInitialForm(moduleAPIName?: string, prefill?: Prefill) {
  return {
    ModuleAPIName: moduleAPIName,
    Prefill: prefill,
    Header: { Name: `${moduleAPIName} Public Form` },
    GeneralStyles: {
      TitleColor: "#000000",
      DescriptionColor: "#000000",
      BackgroundColor: "",
    },
    FormStyles: {
      BorderRadius: 5,
      BackgroundColor: "#ffffff",
      TextColor: "#000000",
    },
    FieldStyles: {
      LabelsColor: "#000000",
      InputBorderColor: "#BDC9C2",
      FocusColor: "#26B058",
      BorderRadius: 4,
      TextColor: "#000000",
      BackgroundColor: "#ffffff",
      FieldsPerRow: "2",
    },
    ButtonStyles: {
      ButtonText: "Submit",
      BorderRadius: 5,
      BackgroundColor: "#26B058",
      TextColor: "#ffffff",
      Size: "Small",
      Position: "Left",
    },
    PostSubmit: {
      ThankYouMessage:
        "Thank you for your submission! Your details have been sent successfully.",
    },
    PageSettings: {
      Title: "",
      Description: "",
    },
  };
}

export default FormBuilderDrawer;
